/**
 * How the air module names things on screen and in a file (#720) — one place
 * for the client (chart legend, table header, station picker, report page) and
 * the server (CSV / Excel headers), so the two can never drift.
 *
 * Rules:
 *   - a parameter is its Slovenian name (»Hitrost vetra«), never its id;
 *   - a station is its name, an unbound map point is the point's own name —
 *     an internal id is never printed;
 *   - with two stations on one chart every series says which station it is.
 *
 * Pure, no DOM, no I/O.
 */

import type {
  ArsoParameter,
  ArsoSeries,
  ArsoStationMeta,
  TimeseriesResponse,
} from '../types/ngArso.js';

export const ARSO_PARAMETER_LABELS: Record<ArsoParameter, string> = {
  t: 'Temperatura',
  td: 'Rosišče',
  rh: 'Relativna vlažnost',
  wind_speed: 'Hitrost vetra',
  wind_dir: 'Smer vetra',
  padavine: 'Padavine',
  global_solar: 'Sončno sevanje',
  pressure: 'Zračni tlak',
  pm1: 'PM1',
  pm10: 'PM10',
  'pm2.5': 'PM2.5',
  no2: 'NO₂',
  nox: 'NOx',
  o3: 'O₃',
  so2: 'SO₂',
  co: 'CO',
  benzen: 'Benzen',
};

/** What a map point without a name reads (it cannot happen: every point has one). */
export const ARSO_UNNAMED_POINT = 'Merilno mesto';

/** Said of a station whose place is not known (no geometry stored). */
export const ARSO_NO_LOCATION_NOTE = 'lokacija še ni potrjena';

/** Joins a parameter name and a station name in a legend entry or a column header. */
const STATION_SEPARATOR = ' — ';

/**
 * A series' own name: the label the server gave it (the traffic overlay), else
 * the parameter's Slovenian name. The id is the last resort for a key this
 * build does not know yet.
 */
export function arsoSeriesLabel(s: Pick<ArsoSeries, 'parameter' | 'label'>): string {
  if (s.label) return s.label;
  return (ARSO_PARAMETER_LABELS as Record<string, string | undefined>)[s.parameter] ?? s.parameter;
}

/** A station as people read it: its name; an unbound point's own name. Never an id. */
export function arsoStationLabel(station: ArsoStationMeta): string {
  if (station.out_of_pilot) return station.sensor_name?.trim() || ARSO_UNNAMED_POINT;
  return station.name;
}

/** A station in the picker: its name, marked when nobody knows yet where it stands. */
export function arsoStationOptionLabel(s: { name: string; has_location: boolean }): string {
  return s.has_location ? s.name : `${s.name}${STATION_SEPARATOR}${ARSO_NO_LOCATION_NOTE}`;
}

/** A series ready to show: its final label, and whether it belongs to the second station. */
export interface ArsoDisplaySeries extends ArsoSeries {
  label: string;
  /** True for a series of the comparison (second) station. */
  second: boolean;
}

/**
 * The series of an answer in display order, with their final labels — what the
 * chart, the table, the CSV and the Excel sheet all show.
 *
 * One station: the series as asked, named by parameter. Two stations: per
 * parameter the first station's series, then the second's, each named
 * »<parameter> — <station>«. The traffic overlay (a series with its own label)
 * comes last and keeps its label. Empty series are NOT removed here: every
 * caller hides them the same way it always did.
 */
export function arsoDisplaySeries(
  response: Pick<TimeseriesResponse, 'station' | 'series' | 'compare'>,
): ArsoDisplaySeries[] {
  const compare = response.station.out_of_pilot ? undefined : response.compare;
  const firstName = arsoStationLabel(response.station);

  const measured = response.series.filter((s) => s.parameter !== 'promet_motor');
  const overlays = response.series.filter((s) => s.parameter === 'promet_motor');

  const named = (s: ArsoSeries, stationName: string | null, second: boolean): ArsoDisplaySeries => ({
    ...s,
    label: stationName === null ? arsoSeriesLabel(s) : `${arsoSeriesLabel(s)}${STATION_SEPARATOR}${stationName}`,
    second,
  });

  const out: ArsoDisplaySeries[] = [];
  if (!compare) {
    for (const s of measured) out.push(named(s, null, false));
  } else {
    const secondName = compare.station.name;
    const rest = [...compare.series];
    for (const s of measured) {
      out.push(named(s, firstName, false));
      const at = rest.findIndex((c) => c.parameter === s.parameter);
      if (at >= 0) out.push(named(rest.splice(at, 1)[0] as ArsoSeries, secondName, true));
    }
    // A parameter only the second station answered (the server asks both for the same list).
    for (const c of rest) out.push(named(c, secondName, true));
  }
  for (const o of overlays) out.push(named(o, null, false));
  return out;
}
